import React, { useState, useEffect, useRef, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import DropZone from '../common/DropZone';
import { MenuItem, Select, Radio, RadioGroup, FormControlLabel, Tooltip, Alert } from '@mui/material';
import Dropzone from 'react-dropzone';
import Chip from '@mui/material/Chip';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import {
  send_request,
  send_external_request,
  send_component_save_request,
} from '../../utils/Request';
import useDocumentStore, { getDocumentStore } from './useDocumentStore';
import styles from './DocumentStyle';
import { getCurrentTime } from '../../utils/getCurrentTime';
import Icon from '@mui/material/Icon';
import checkKey from '../../utils/CheckKey';
import DocumentEditor from '../TextEditor/DocumentEditor';
import CircularProgress from '@mui/material/CircularProgress';
import CONFIG from '../../config';
import replacePlaceholderValue from '../../utils/ReplacePlaceholderValue';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import DocumentFilePlaceholderUpload from '../TextEditor/DocumentFilePlaceholderUpload';
import W86PlaceholderTextField from '../W86Components/W86PlaceholderTextField';
import DocumentPreview from './DocumentPreview';
import './DocumentEditor.css';
import CustomPaper from '../common/CustomPaper';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';

const fileService = CONFIG.FILE_SERVICE;

const sx = {
  verticalContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  dropzone: {
    width: '100%',
    margin: 'auto',
  },
  customIcon: {
    color: 'text.secondary',
  },
  chipStyle: {
    backgroundColor: 'rgba(33, 150, 243, 0.6)',
    '&:hover': {
      backgroundColor: '#ED6696',
    },
  },
};

const DocumentTemplate = (props) => {
  const [focus, _setFocus] = useState(null);
  const focusRef = useRef(null);
  const setFocus = (val) => {
    _setFocus(val);
    focusRef.current = val;
  };
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkKeyError, setCheckKeyError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fileUrl, setFileUrlState] = useState([]);
  const [status, setStatus] = useState(null);
  const [fileId, setFileId] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);

  const store = useMemo(() => getDocumentStore(props.component_id), [props.component_id]);
  const data = store(s => s.data);
  const files = store(s => s.files);
  const storeFileUrl = store(s => s.fileUrl);

  useEffect(() => {
    if (props.component_id === undefined) return;

    // If store was prefilled from canvas load, skip the REST fetch
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      const storeFiles = store.getState().files;
      setFileId(storeFiles && storeFiles.length > 0 ? storeFiles[0].id : '');

      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    // Fallback: fetch from REST
    setLoading(true);
    const url = `project-service/project/component/${props.component_id}`;
    send_request(url, '', {})
      .then(json => {
        if (json && json.data) {
          const components = json.data;
          if (components) {
            store.getState().setData(components);
            store.setState({ loaded: true });
            props.onComponentNameChanged(store.getState().name);
            props.onComponentDescriptionChanged(components.description);
            props.onLastModifiedChanged(store.getState().lastModified);
          }
        }

        const storeFiles = store.getState().files;
        setLoading(false);
        setFileId(storeFiles && storeFiles.length > 0 ? storeFiles[0].id : '');
        props.setChildTemplateLoaded(true);
      })
      .catch(err => {
        throw Error(err.message);
      });
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      s => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected'] || typeof placeholder['selected'] !== 'object') return;
        const { style, key } = placeholder['selected'];
        if (!style) return;
        insertPlaceholder(style.default, key, style, focusRef.current);
      }
    );
    return unsub;
  }, []);

  const insertPlaceholder = (label, text, style, currentFocus) => {
    if (currentFocus === 'editor' || currentFocus === 'placeholder') {
      setPlaceholders(prev => [...prev, { label, text, style, focus: currentFocus }]);
    } else {
      toInput(label, text, style, currentFocus);
    }
  };

  const toInput = (label, text, style, currentFocus) => {
    // placeholder insertion into non-editor fields (no-op in original for this component)
  };

  const onFilesDrop = async (droppedFiles) => {
    if (props.project_id || props.projectId) {
      setUploadLoading(true);

      let formData = new FormData();
      formData.append('file', droppedFiles[0]);
      if (props.project_id) {
        formData.append('projectId', props.project_id);
      } else {
        formData.append('projectId', props.projectId);
      }

      try {
        const json = await send_request(
          'component-service/document-editor/upload',
          formData,
          '',
          'POST',
          { 'content-type': 'multipart/form-data' }
        );
        if (json && json.data) {
          let { fileName, id } = json.data;
          store.getState().setFiles([{ fileName, id }]);
          setFileId(id);
          setUploadLoading(false);
        }
      } catch (err) {
        throw err;
      }
    }
  };

  const handleKeyChange = e => {
    const placeholderValue = replacePlaceholderValue(e.target.value);
    store.getState().setContent(placeholderValue, 'key');

    const storeData = store.getState().data;
    const value = checkKey(placeholderValue, storeData);

    setCheckKeyError(value[0]);
    setErrorMessage(value[1]);
  };

  const handleChangeUrl = chip => {
    const storeData = store.getState().data;
    let key = chip.key;
    if (chip.isNotPlaceholder !== true) {
      key = '${' + key + '}';
    }
    const updateField = [key];
    setFileUrlState(updateField);
    store.getState().setFileUrl(updateField);
  };

  if (!loading) {
    return (
      <div className="editor-container">
        <CustomPaper>
          <div style={sx.verticalContainer}>
            <div className="section">
              <div className="label">Placeholder</div>
              <div className="placeholder-input">
                <W86PlaceholderTextField
                  value={data.key}
                  error={checkKeyError}
                  helperText={checkKeyError ? errorMessage : null}
                  placeholderText="Enter placeholder"
                  handleChange={handleKeyChange}
                />
                <Tooltip
                  title="Output will be file URL of the generated document"
                  placement="right-start"
                  componentsProps={{
                    tooltip: {
                      sx: { fontSize: 12, p: '8px 16px', fontFamily: 'Open Sans', bgcolor: 'rgba(0,0,0,0.7)', borderRadius: '6px' }
                    }
                  }}
                >
                  <HelpOutlineOutlinedIcon sx={sx.customIcon} />
                </Tooltip>
              </div>
            </div>

            <div className="section">
              <div className="label">Create a document by</div>
              <RadioGroup
                className="radio-group"
                name="selectType"
                value={data.type}
                onChange={event => {
                  setLoading(true);
                  store.getState().setContent(event.target.value, 'type');
                  setLoading(false);
                }}
                row
              >
                <FormControlLabel value="EDITOR" control={<Radio />} label="built-in editor" />
                <FormControlLabel
                  value="DOCX"
                  control={<Radio />}
                  label="uploading .docx template"
                />
                <FormControlLabel value="URL" control={<Radio />} label="via placeholder" />
              </RadioGroup>
            </div>

            <div className="section">
              <div className="label">Output format</div>
              <RadioGroup
                className="radio-group"
                name="selectType"
                value={data.export}
                onChange={event => {
                  setLoading(true);
                  store.getState().setContent(event.target.value, 'export');
                  setLoading(false);
                }}
                row
              >
                <FormControlLabel value="DOCX" control={<Radio />} label=".docx" />
                <FormControlLabel value="PDF" control={<Radio />} label=".pdf" />
              </RadioGroup>
            </div>
          </div>
        </CustomPaper>
        {data.type == 'EDITOR' ? (
          <CustomPaper style={{ marginTop: '16px' }}>
            <DocumentEditor
              availablePlaceholders={props.availablePlaceholders}
              html={data.content}
              onChange={html => {
                store.getState().setContent(html, 'content');
              }}
              placeholder={placeholders}
              onFocus={() => {
                setFocus('editor');
              }}
              isExpanded={props.isExpanded}
              documentEditor
            />
          </CustomPaper>
        ) : data.type == 'DOCX' ? (
          <CustomPaper style={{ marginTop: '16px' }}>
            <h4 className="upload-title">Upload a .docx template</h4>
            <div className="upload-info">
              <span>
                Generate documents by merging placeholders into an uploaded .docx template.{' '}
                <a
                  href="https://docs.workflow86.com/docs/components/doc_editor#generate-documents-using-a-docx-template"
                  target="_blank"
                  className="a-href"
                >
                  Learn more here
                </a>
              </span>
            </div>
            <div style={sx.dropzone}>
              {uploadLoading ? (
                <div className="upload-loading">
                  <CircularProgress size={24} className="upload-loading-spinner" />
                  <span>Uploading file...</span>
                </div>
              ) : (
                <div>
                  <Dropzone
                    style={{ width: '100%' }}
                    multiple={false}
                    onDrop={acceptedFiles => onFilesDrop(acceptedFiles)}
                    accept=".docx,.doc"
                  >
                    {({ getRootProps, getInputProps }) => (
                      <section>
                        <div {...getRootProps()} style={{}}>
                          <DropZone />
                          <input type="file" {...getInputProps()} accept=".docx,.doc" />
                        </div>
                      </section>
                    )}
                  </Dropzone>
                  <div className="file-upload-container">
                    {files && files.length > 0 ? (
                      <Chip
                        sx={sx.chipStyle}
                        label={
                          <span className="chip-container">
                            {decodeURIComponent(files[0].fileName)}
                          </span>
                        }
                        onDelete={() => {
                          store.getState().setFiles([]);
                          setUploadLoading(false);
                          setFileId('');
                        }}
                        onClick={() => {
                          const currentFiles = store.getState().files;
                          const url = `${fileService.QUERY_URL}` + '/' + currentFiles[0].id;
                          window.location.href = url;
                        }}
                        color="primary"
                      />
                    ) : (
                      <Alert
                        severity="warning"
                        sx={{ mt: 1, fontSize: 13, '& .MuiAlert-icon': { fontSize: 18 } }}
                      >
                        No template uploaded — this component will fail at runtime without a .docx file.
                      </Alert>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CustomPaper>
        ) : (
          <CustomPaper style={{ marginTop: '16px' }}>
            <DocumentFilePlaceholderUpload
              availablePlaceholders={props.availablePlaceholders}
              onPlaceholderSelected={handleChangeUrl}
              placeholder={placeholders}
              onFocus={() => {
                setFocus('placeholder');
              }}
              defaultValues={storeFileUrl}
            />
            {(!storeFileUrl || storeFileUrl.length === 0 || !storeFileUrl[0]) && (
              <Alert
                severity="warning"
                sx={{ mt: 2, fontSize: 13, '& .MuiAlert-icon': { fontSize: 18 } }}
              >
                No document URL provided — this component will fail at runtime without a file URL or placeholder.
              </Alert>
            )}
          </CustomPaper>
        )}
        {data.type === 'DOCX' &&
          files &&
          files.length > 0 &&
          !loading &&
          fileId &&
          !uploadLoading && (
            <div>
              <DocumentPreview filePath={fileId} />
            </div>
          )}
      </div>
    );
  } else return <IndividualComponentLoader />;
};

export default DocumentTemplate;
